// Final Analytical Review Card V2 — shared pieces of the review screen: number / percent formats, the remark cell (typed remark,
// "AI draft — confirm" marker, Confirm, Draft explanation) and the links to the work done (W9, risks, KAMs). EN + AR from
// lib/i18n/1160_far.js. Batch 33 · FAR-F5: the cells only change the screen; the screen's one Save writes.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { asArray } from "@/lib/asArray";
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter

export const fmt = (n) => {
  if (n === null || n === undefined || n === "" || Number.isNaN(Number(n))) return "—";
  return fmtNumber(Number(n));
};

export const fmtPct = (n) => {
  if (n === null || n === undefined || n === "" || Number.isNaN(Number(n))) return "—";
  return `${fmtNumber(Number(n), { decimals: 1 })}%`;
};

// "{n} lines" style placeholders (the i18n t() has no interpolation)
export const fill = (s, vars) => Object.entries(vars || {}).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(String(v)), s || "");

export const L = (o, k, lang) => (lang === "ar" && o?.[`${k}_ar`]) || o?.[k] || "";

export const safeKey = (k) => String(k).replace(/[^A-Za-z0-9_.-]/g, "_");

// Batch 33 · FAR-F5: the remark is part of the screen's one Save — typing, drafting and confirming change the screen only
// (``remark`` = what is shown: the saved remark with the unsaved change on top); nothing is sent from the cell itself.
export function RemarkCell({ itemKey, remark, t, locked, onChange, onConfirm, onDraft, busy }) {
  const safe = safeKey(itemKey);
  return (
    <div className="min-w-[220px] space-y-1" data-testid={`far-remark-${safe}`}>
      <textarea className="w-full rounded border px-2 py-1 text-xs disabled:opacity-60 dark:bg-slate-900" rows={2}
                value={remark?.text || ""} placeholder={t("far.remarkPlaceholder")} disabled={locked}
                onChange={(e) => onChange(itemKey, e.target.value)} data-testid={`far-remark-input-${safe}`} />
      {remark?.ai_draft && (
        <div className="flex items-center gap-2">
          <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-800 dark:bg-violet-950 dark:text-violet-200"
                data-testid={`far-ai-draft-${safe}`}>{t("far.aiDraftMarker")}</span>
          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={locked || busy}
                  onClick={() => onConfirm(itemKey)} data-testid={`far-confirm-${safe}`}>{t("far.confirm")}</Button>
        </div>
      )}
      {onDraft && (
        <div className="flex flex-wrap gap-1">
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" disabled={locked || busy}
                  onClick={() => onDraft([itemKey])} data-testid={`far-draft-${safe}`}>{t("far.draftOne")}</Button>
        </div>
      )}
    </div>
  );
}

// Batch 33 · B33-3 — the Work done cell: the automatic links (W9, risks, KAMs) stay above; the free text under them is part of
// the screen's one Save (FAR-F5); read-only when locked.
export function WorkDoneCell({ itemKey, links, workDone, t, locked, onChange }) {
  const safe = safeKey(itemKey);
  return (
    <div className="min-w-[200px] space-y-1" data-testid={`far-workdone-${safe}`}>
      <LinkChips links={links} t={t} testid={`far-links-${itemKey}`} />
      <textarea className="w-full rounded border px-2 py-1 text-xs disabled:opacity-60 dark:bg-slate-900" rows={2}
                value={workDone?.text || ""} placeholder={t("far.workDonePlaceholder")} disabled={locked}
                onChange={(e) => onChange(itemKey, e.target.value)} data-testid={`far-workdone-input-${safe}`} />
    </div>
  );
}

// Batch 33 · B33-3 — column widths adjustable by dragging the header's edge, remembered per user in this browser
// (localStorage; a blocked / missing storage just means the defaults every time).
// PR 51 · P51-S4: in Arabic (RTL) the column's edge sits on its left, so dragging LEFT widens it — the drag direction is flipped.
export function useColumnWidths(storageKey, defaults, rtl = false) {
  const read = () => {
    try { return { ...defaults, ...(JSON.parse(window.localStorage.getItem(storageKey) || "{}") || {}) }; }
    catch (e) { return { ...defaults }; }
  };
  const [widths, setWidths] = useState(read);
  const ref = useRef(widths);
  ref.current = widths;
  // the stored widths are re-read only when the key (the user) changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setWidths(read()); }, [storageKey]);
  const startDrag = useCallback((col, e) => {
    e.preventDefault(); e.stopPropagation();
    const x0 = e.clientX;
    const w0 = ref.current[col] || defaults[col] || 120;
    const delta = (ev) => (rtl ? x0 - ev.clientX : ev.clientX - x0);
    const move = (ev) => setWidths((w) => ({ ...w, [col]: Math.max(60, Math.round(w0 + delta(ev))) }));
    const up = (ev) => {
      window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      const next = { ...ref.current, [col]: Math.max(60, Math.round(w0 + delta(ev))) };
      setWidths(next);
      try { window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch (err) { /* storage blocked: kept for this view */ }
    };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  }, [storageKey, defaults, rtl]);
  // Step 11d · add-on 4: "Reset column widths" — back to the defaults, the stored widths cleared
  const reset = useCallback(() => {
    setWidths({ ...defaults });
    try { window.localStorage.removeItem(storageKey); } catch (err) { /* storage blocked */ }
  }, [storageKey, defaults]);
  return [widths, startDrag, reset];
}

export function ResizeHandle({ col, onStart }) {
  return (
    <span role="separator" aria-orientation="vertical" onMouseDown={(e) => onStart(col, e)}
          className="absolute inset-y-0 end-0 w-1.5 cursor-col-resize select-none hover:bg-sky-300"
          data-testid={`far-col-resize-${col}`} />
  );
}

export function LinkChips({ links, t, testid }) {
  const groups = [["misstatements", t("far.links.misstatements")], ["risks", t("far.links.risks")], ["kams", t("far.links.kams")]];
  const any = groups.some(([k]) => asArray(links?.[k]).length > 0);
  if (!any) return <span className="text-[11px] text-muted-foreground" data-testid={testid}>{t("far.links.none")}</span>;
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {groups.map(([k, label]) => asArray(links?.[k]).map((x) => (
        <Link key={`${k}-${x.id}`} to={x.path || "#"} title={`${label}: ${x.title || x.ref || ""}`}
              className="rounded border px-1.5 py-0.5 text-[10px] hover:bg-slate-100 dark:hover:bg-slate-800"
              data-testid={`${testid}-${k}-${x.id}`}>
          {label}: {x.title ? (x.title.length > 48 ? `${x.title.slice(0, 47)}…` : x.title) : (x.ref || x.id)}
        </Link>
      )))}
    </div>
  );
}
